import { useState, useCallback, useRef, type DragEvent, type ChangeEvent } from 'react';
import { useImportPreview, useExecuteImport } from '@/hooks/useImport';
import {
  detectFileType,
  type ParseResult,
  type ImportReport,
  type ConflictStrategy,
  type ImportFileType,
} from '@/api/import.api';

// ─── Props ──────────────────────────────────────────────────────────────────

interface ImportModalProps {
  onClose: () => void;
}

// ─── Schritte ───────────────────────────────────────────────────────────────

type Step = 1 | 2 | 3 | 4;

const STEP_LABELS: Record<Step, string> = {
  1: 'Datei auswählen',
  2: 'Vorschau',
  3: 'Importieren',
  4: 'Bericht',
};

// ─── Stepper-Anzeige ────────────────────────────────────────────────────────

function Stepper({ currentStep }: { currentStep: Step }) {
  const steps = [1, 2, 3, 4] as const;

  return (
    <div className="flex items-center gap-1 px-6 py-3">
      {steps.map((step, idx) => (
        <div key={step} className="flex items-center">
          <div className="flex items-center gap-2">
            <div
              className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                step < currentStep
                  ? 'bg-green-100 text-green-700'
                  : step === currentStep
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-400'
              }`}
            >
              {step < currentStep ? (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              ) : (
                step
              )}
            </div>
            <span
              className={`hidden text-xs sm:inline ${
                step === currentStep ? 'font-medium text-slate-800' : 'text-slate-400'
              }`}
            >
              {STEP_LABELS[step]}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div
              className={`mx-2 h-px w-6 ${
                step < currentStep ? 'bg-green-300' : 'bg-slate-200'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Dateityp-Badge ─────────────────────────────────────────────────────────

function FileTypeBadge({ fileType }: { fileType: ImportFileType }) {
  const config = fileType === 'gedcom'
    ? { label: 'GEDCOM', bg: 'bg-violet-100', text: 'text-violet-700' }
    : { label: 'CSV', bg: 'bg-teal-100', text: 'text-teal-700' };

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

// ─── Schritt 1: Datei-Auswahl ──────────────────────────────────────────────

function StepFileSelect({
  onFileSelected,
}: {
  onFileSelected: (file: File) => void;
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);

      const file = e.dataTransfer.files[0];
      if (file) onFileSelected(file);
    },
    [onFileSelected],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) onFileSelected(file);
    },
    [onFileSelected],
  );

  return (
    <div className="flex flex-col items-center gap-4 px-6 py-8">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`flex w-full cursor-pointer flex-col items-center gap-3 rounded-lg border-2 border-dashed p-6 transition-colors sm:p-10 ${
          isDragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-white'
        }`}
        onClick={() => fileInputRef.current?.click()}
      >
        <svg
          className={`h-10 w-10 ${isDragOver ? 'text-blue-500' : 'text-slate-400'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
          />
        </svg>
        <div className="text-center">
          <p className="text-sm font-medium text-slate-700">
            Datei hierher ziehen oder klicken
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Akzeptiert: .ged (GEDCOM 5.5.1), .csv &middot; Max. 10 MB
          </p>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".ged,.gedcom,.csv"
        onChange={handleInputChange}
        className="hidden"
      />
    </div>
  );
}

// ─── Schritt 1b: Datei ausgewählt → Upload starten ─────────────────────────

function StepFileSelected({
  file,
  fileType,
  isLoading,
  error,
  onUpload,
  onReset,
}: {
  file: File;
  fileType: ImportFileType;
  isLoading: boolean;
  error: string | null;
  onUpload: () => void;
  onReset: () => void;
}) {
  const fileSizeKb = (file.size / 1024).toFixed(1);

  return (
    <div className="flex flex-col gap-4 px-6 py-6">
      <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <svg className="h-8 w-8 shrink-0 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium text-slate-800">{file.name}</p>
            <FileTypeBadge fileType={fileType} />
          </div>
          <p className="text-xs text-slate-400">{fileSizeKb} KB</p>
        </div>
        <button
          onClick={onReset}
          disabled={isLoading}
          className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 disabled:opacity-50"
          title="Andere Datei wählen"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <button
        onClick={onUpload}
        disabled={isLoading}
        className="flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
      >
        {isLoading ? (
          <>
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Datei wird analysiert...
          </>
        ) : (
          <>
            Weiter
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </>
        )}
      </button>
    </div>
  );
}

// ─── Schritt 2: Vorschau ────────────────────────────────────────────────────

function StepPreview({
  preview,
  fileType,
  strategy,
  onStrategyChange,
  onImport,
  onBack,
}: {
  preview: ParseResult;
  fileType: ImportFileType;
  strategy: ConflictStrategy;
  onStrategyChange: (s: ConflictStrategy) => void;
  onImport: () => void;
  onBack: () => void;
}) {
  const familyCount = fileType === 'gedcom'
    ? new Set(preview.familyMembers.map((fm) => fm.family_xref).filter(Boolean)).size
      || preview.relationships.length
    : preview.familyMembers.length;

  return (
    <div className="flex flex-col gap-4 px-6 py-4">
      {/* Zähler */}
      <div className="flex gap-3">
        <div className="flex-1 rounded-lg bg-blue-50 p-3 text-center">
          <p className="text-2xl font-bold text-blue-700">{preview.persons.length}</p>
          <p className="text-xs text-blue-600">Personen</p>
        </div>
        <div className="flex-1 rounded-lg bg-emerald-50 p-3 text-center">
          <p className="text-2xl font-bold text-emerald-700">
            {fileType === 'gedcom' ? familyCount : preview.familyMembers.length}
          </p>
          <p className="text-xs text-emerald-600">
            {fileType === 'gedcom' ? 'Familien' : 'Eltern-Zuordnungen'}
          </p>
        </div>
        <div className="flex-1 rounded-lg bg-amber-50 p-3 text-center">
          <p className="text-2xl font-bold text-amber-700">{preview.errors.length}</p>
          <p className="text-xs text-amber-600">Fehler</p>
        </div>
      </div>

      {/* Personen-Tabelle */}
      <div className="max-h-56 overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Geburt</th>
              <th className="px-3 py-2">Tod</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {preview.persons.slice(0, 100).map((p, idx) => (
              <tr key={idx} className="hover:bg-slate-50">
                <td className="px-3 py-1.5 font-medium text-slate-800">
                  {p.first_name} {p.last_name ?? ''}
                </td>
                <td className="px-3 py-1.5 text-slate-500">
                  {p.birth_date_text ?? ''}
                  {p.birth_place ? `, ${p.birth_place}` : ''}
                </td>
                <td className="px-3 py-1.5 text-slate-500">
                  {p.death_date_text ?? ''}
                  {p.death_place ? `, ${p.death_place}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {preview.persons.length > 100 && (
          <p className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-center text-xs text-slate-400">
            … und {preview.persons.length - 100} weitere Personen
          </p>
        )}
      </div>

      {/* Fehler-Liste */}
      {preview.errors.length > 0 && (
        <details className="rounded-lg border border-amber-200 bg-amber-50">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-amber-800">
            {preview.errors.length} Fehler beim Parsen
          </summary>
          <ul className="max-h-32 overflow-auto px-3 pb-2 text-xs text-amber-700">
            {preview.errors.map((err, idx) => (
              <li key={idx} className="mt-1">
                &bull; {err}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Konflikt-Strategie */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Bei vorhandenen Personen:
        </p>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="strategy"
              value="skip"
              checked={strategy === 'skip'}
              onChange={() => onStrategyChange('skip')}
              className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            Überspringen
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="strategy"
              value="overwrite"
              checked={strategy === 'overwrite'}
              onChange={() => onStrategyChange('overwrite')}
              className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            Überschreiben
          </label>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Zurück
        </button>
        <button
          onClick={onImport}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Importieren
        </button>
      </div>
    </div>
  );
}

// ─── Schritt 3: Import läuft ────────────────────────────────────────────────

function StepImporting() {
  return (
    <div className="flex flex-col items-center gap-4 px-6 py-12">
      <div className="h-2 w-64 overflow-hidden rounded-full bg-slate-200">
        <div className="h-full animate-pulse rounded-full bg-blue-500" style={{ width: '75%' }} />
      </div>
      <p className="text-sm text-slate-600">Import läuft – bitte warten...</p>
      <p className="text-xs text-slate-400">Personen, Beziehungen und Familien werden erstellt.</p>
    </div>
  );
}

// ─── Schritt 4: Bericht ─────────────────────────────────────────────────────

/** „4 neu angelegt, 2 aktualisiert" – nur was tatsächlich passiert ist. */
function teile(report: ImportReport): string[] {
  const raus: string[] = [];
  if (report.imported > 0) raus.push(`${report.imported} neu angelegt`);
  if (report.updated > 0) raus.push(`${report.updated} aktualisiert`);
  if (report.skipped > 0) raus.push(`${report.skipped} übersprungen`);
  return raus;
}

function StepReport({
  report,
  onClose,
}: {
  report: ImportReport;
  onClose: () => void;
}) {
  const hasErrors = report.errors.length > 0;

  return (
    <div className="flex flex-col gap-4 px-6 py-6">
      <div
        className={`rounded-lg p-4 text-center ${
          hasErrors ? 'bg-amber-50' : 'bg-green-50'
        }`}
      >
        <div className="text-3xl">
          {hasErrors ? '!' : '\u2713'}
        </div>
        {/* Angelegt und aktualisiert getrennt nennen. Beides zusammen als
            „importiert" zu melden verschweigt genau das, worauf es bei
            „Überschreiben" ankommt: ob die Leute wirklich zusammengeführt
            wurden oder ein zweites Mal danebenstehen. */}
        <p className="mt-2 text-sm font-medium text-slate-800">
          {teile(report).join(', ') || 'Nichts zu tun'}
          {hasErrors && `, ${report.errors.length} Fehler`}
        </p>
      </div>

      {hasErrors && (
        <details className="rounded-lg border border-red-200 bg-red-50">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-red-800">
            {report.errors.length} Fehler anzeigen
          </summary>
          <ul className="max-h-40 overflow-auto px-3 pb-2 text-xs text-red-700">
            {report.errors.map((err, idx) => (
              <li key={idx} className="mt-1">
                &bull; {err}
              </li>
            ))}
          </ul>
        </details>
      )}

      <button
        onClick={onClose}
        className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Fertig
      </button>
    </div>
  );
}

// ─── Haupt-Komponente ───────────────────────────────────────────────────────

export function ImportModal({ onClose }: ImportModalProps) {
  const [step, setStep] = useState<Step>(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<ImportFileType>('gedcom');
  const [preview, setPreview] = useState<ParseResult | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [strategy, setStrategy] = useState<ConflictStrategy>('skip');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const previewMutation = useImportPreview();
  const importMutation = useExecuteImport();

  // ── Datei auswählen (mit Auto-Erkennung) ────────────────────────────────

  const handleFileSelected = useCallback((file: File) => {
    const detected = detectFileType(file.name);
    if (!detected) {
      setUploadError('Ungültiges Dateiformat. Erlaubt: .ged, .csv');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Datei ist größer als 10 MB.');
      return;
    }

    setSelectedFile(file);
    setFileType(detected);
    setUploadError(null);
  }, []);

  // ── Vorschau laden (Schritt 1 → 2) ─────────────────────────────────────

  const handleUpload = useCallback(() => {
    if (!selectedFile) return;

    previewMutation.mutate(
      { file: selectedFile, fileType },
      {
        onSuccess: (result) => {
          setPreview(result);
          setStep(2);
        },
        onError: (error) => {
          setUploadError(error.message || 'Fehler beim Analysieren der Datei.');
        },
      },
    );
  }, [selectedFile, fileType, previewMutation]);

  // ── Import starten (Schritt 2 → 3 → 4) ─────────────────────────────────

  const handleImport = useCallback(() => {
    if (!selectedFile) return;

    setStep(3);

    importMutation.mutate(
      { file: selectedFile, fileType, strategy },
      {
        onSuccess: (result) => {
          setReport(result);
          setStep(4);
        },
        onError: () => {
          setStep(2);
        },
      },
    );
  }, [selectedFile, fileType, strategy, importMutation]);

  // ── Zurücksetzen ────────────────────────────────────────────────────────

  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setPreview(null);
    setReport(null);
    setUploadError(null);
    setStep(1);
  }, []);

  const handleBackdropClick = useCallback(() => {
    if (step !== 3) onClose();
  }, [step, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && step !== 3) onClose();
    },
    [step, onClose],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="Daten importieren"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Daten importieren</h2>
          {step !== 3 && (
            <button
              onClick={onClose}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <Stepper currentStep={step} />

        <div className="flex-1 overflow-y-auto">
          {step === 1 && !selectedFile && (
            <StepFileSelect onFileSelected={handleFileSelected} />
          )}

          {step === 1 && selectedFile && (
            <StepFileSelected
              file={selectedFile}
              fileType={fileType}
              isLoading={previewMutation.isPending}
              error={uploadError}
              onUpload={handleUpload}
              onReset={handleReset}
            />
          )}

          {step === 2 && preview && (
            <StepPreview
              preview={preview}
              fileType={fileType}
              strategy={strategy}
              onStrategyChange={setStrategy}
              onImport={handleImport}
              onBack={handleReset}
            />
          )}

          {step === 3 && <StepImporting />}

          {step === 4 && report && (
            <StepReport report={report} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
}
